import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { AnalyticsTracker } from "./hooks/useAnalytics";

// Main pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import AuthCallback from "./pages/AuthCallback";

// Static pages
import {
  ParentAppPage,
  AccessoriesPage,
  PrivacyPage,
  TermsPage,
  RefundPage,
  CareersPage,
  BlogPage,
  BlogArticlePage,
  OurStoryPage,
  TeamPage,
  PressPage,
  ContactPage,
} from "./pages/static";

// Lazy load Admin (heavy component)
const Admin = lazy(() => import("./pages/Admin"));

// Loading fallback for lazy components
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-yellow-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Chargement...</p>
    </div>
  </div>
);

// App Router with session_id check
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id - CRITICAL: Must be synchronous for Google Auth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Main pages */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Admin - Lazy loaded */}
        <Route path="/admin" element={<Admin />} />
        
        {/* Product pages */}
        <Route path="/app-parent" element={<ParentAppPage />} />
        <Route path="/accessoires" element={<AccessoriesPage />} />
        
        {/* Company pages */}
        <Route path="/notre-histoire" element={<OurStoryPage />} />
        <Route path="/equipe" element={<TeamPage />} />
        <Route path="/presse" element={<PressPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/carrieres" element={<CareersPage />} />
        
        {/* Blog */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        
        {/* Legal pages */}
        <Route path="/confidentialite" element={<PrivacyPage />} />
        <Route path="/cgv" element={<TermsPage />} />
        <Route path="/remboursement" element={<RefundPage />} />
      </Routes>
    </Suspense>
  );
};

// Main App Component
function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsTracker>
            <AppRouter />
          </AnalyticsTracker>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
